import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type RouteRecordRaw,
} from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy-import every page chunk so the initial JS bundle only carries
// the shell + login. The other pages stream in as they're navigated to.
const LoginPage = () => import('@/pages/LoginPage.vue')
const ChangePasswordPage = () => import('@/pages/ChangePasswordPage.vue')
const DashboardPage = () => import('@/pages/DashboardPage.vue')
const EntriesPage = () => import('@/pages/EntriesPage.vue')
const EntryDetailPage = () => import('@/pages/EntryDetailPage.vue')
const StatsLayout = () => import('@/pages/stats/StatsLayout.vue')
const ConfusionMatrixPage = () => import('@/pages/stats/ConfusionMatrixPage.vue')
const ConfidencePage = () => import('@/pages/stats/ConfidencePage.vue')
const DevicesPage = () => import('@/pages/stats/DevicesPage.vue')
const FeedbackQueuePage = () => import('@/pages/FeedbackQueuePage.vue')
const FeedbackDetailPage = () => import('@/pages/FeedbackDetailPage.vue')
const FlagsPage = () => import('@/pages/FlagsPage.vue')
const ConfigPage = () => import('@/pages/ConfigPage.vue')
const AuditPage = () => import('@/pages/AuditPage.vue')
const SettingsPage = () => import('@/pages/SettingsPage.vue')
const StatusPage = () => import('@/pages/StatusPage.vue')
const MetricsPage = () => import('@/pages/MetricsPage.vue')
const AppShell = () => import('@/components/AppShell.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
    meta: { public: true },
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: ChangePasswordPage,
    // Authenticated but NOT inside AppShell — we don't want the
    // sidebar tempting the user to navigate away before they rotate.
    meta: { allowExpired: true },
  },
  {
    path: '/',
    component: AppShell,
    children: [
      { path: '', name: 'dashboard', component: DashboardPage },
      { path: 'entries', name: 'entries', component: EntriesPage },
      { path: 'entries/:id', name: 'entry-detail', component: EntryDetailPage },
      {
        path: 'stats',
        component: StatsLayout,
        children: [
          { path: '', redirect: '/stats/confusion' },
          { path: 'confusion', name: 'stats-confusion', component: ConfusionMatrixPage },
          { path: 'confidence', name: 'stats-confidence', component: ConfidencePage },
          { path: 'devices', name: 'stats-devices', component: DevicesPage },
        ],
      },
      { path: 'feedback', name: 'feedback', component: FeedbackQueuePage },
      { path: 'feedback/:id', name: 'feedback-detail', component: FeedbackDetailPage },
      { path: 'flags', name: 'flags', component: FlagsPage },
      { path: 'config', name: 'config', component: ConfigPage },
      { path: 'audit', name: 'audit', component: AuditPage },
      { path: 'status', name: 'status', component: StatusPage },
      { path: 'metrics', name: 'metrics', component: MetricsPage },
      { path: 'settings', name: 'settings', component: SettingsPage },
    ],
  },
  { path: '/:catchAll(.*)', redirect: '/' },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

/**
 * beforeEach guard. Three rules in priority order:
 *  1. Public routes (only /login) pass through with no auth check.
 *  2. Authed routes require a session; if missing → /login (carrying
 *     the original path in ?next= so we redirect back on success).
 *  3. If mustChangePassword=true and we're NOT on /change-password,
 *     bounce there. The route's meta.allowExpired keeps us OUT of the
 *     redirect loop.
 *
 * Exported so the bootstrap-race test (router/index.test.ts) can drive
 * it directly without spinning up a full router + lazy components.
 */
export async function beforeEachGuard(
  to: RouteLocationNormalized,
): Promise<boolean | RouteLocationRaw> {
  const auth = useAuthStore()

  // Always await bootstrap, never gate on `phase === 'idle'`. Why:
  // App.vue's onMounted also fires `auth.bootstrap()` in parallel,
  // which flips phase 'idle' → 'loading' *before* the guard runs. The
  // old `phase === 'idle'` check would then skip the await, see
  // isAuthenticated=false (phase is still 'loading'), and redirect to
  // /login despite a perfectly valid cookie. bootstrap() is internally
  // idempotent — case (1) waits on the existing inflight promise,
  // case (2) returns immediately when phase already terminal — so
  // calling it every time is the cheap, correct fix.
  try { await auth.bootstrap() } catch { /* surfaced by the LoginPage */ }

  if (to.meta.public === true) {
    // If we're authenticated and hitting /login, hop to /.
    if (auth.isAuthenticated && to.name === 'login') return { name: 'dashboard' }
    return true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } }
  }

  if (auth.mustChangePassword && to.meta.allowExpired !== true) {
    return { name: 'change-password' }
  }

  // Authenticated + not forced-change → if they wander onto
  // /change-password without needing to, send them home.
  if (!auth.mustChangePassword && to.name === 'change-password') {
    return { name: 'dashboard' }
  }

  return true
}

router.beforeEach(beforeEachGuard)
